import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import Paragraph from '../../Paragraph/Paragraph';
import Modal from '../../Modal';
import themeColors from '../../../assets/styles/themeColors';
import { mediaBelow, pxToRem, pxToRemMd } from '../../../assets/styles/helper';
import { variables } from '../../../assets/styles/variables';
import { ReactComponent as Success } from '../../../assets/images/svg/success-invitation.svg';
import { ReactComponent as Close } from '../../../assets/images/svg/close-white.svg';
import ButtonPrimary from '../../Buttons/Button';
import { logoutUser } from '../../../store/actions/user/userActions';

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px 10px;
  align-items: center;
  @media only screen and (max-width: 750px) {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 10px 10px;
    margin-top: auto;
    margin-bottom: 24px;
  }
`;
const SaveAndCloseSuccess = styled.div`
  background-color: ${themeColors.colorSuccess};
  display: flex;
  padding: ${pxToRem(20)} ${pxToRem(22)};
  justify-content: center;
  align-items: center;
  ${mediaBelow(variables.breakpoints.bpXl)} {
    padding: ${pxToRemMd(20)} ${pxToRemMd(22)};
  }
`;

const SaveAndCloseError = styled.div`
  background-color: ${themeColors.colorErrorMessage};
  display: flex;
  padding: ${pxToRem(20)} ${pxToRem(22)};
  justify-content: center;
  align-items: center;
  ${mediaBelow(variables.breakpoints.bpXl)} {
    padding: ${pxToRemMd(20)} ${pxToRemMd(22)};
  }
`;

const SaveAndCloseSuccessIcon = styled(Success)`
  width: ${pxToRem(50)};
  height: ${pxToRem(50)};
  color: ${themeColors.colorSuccess};

  ${mediaBelow(variables.breakpoints.bpXl)} {
    width: ${pxToRemMd(50)};
    height: ${pxToRemMd(50)};
    margin-right: ${pxToRemMd(8)};
  }
`;

const SaveAndCloseErrorIcon = styled(Close)`
  width: ${pxToRem(50)};
  height: ${pxToRem(50)};
  color: ${themeColors.colorErrorMessage};

  ${mediaBelow(variables.breakpoints.bpXl)} {
    width: ${pxToRemMd(50)};
    height: ${pxToRemMd(50)};
    margin-right: ${pxToRemMd(8)};
  }
`;

const SaveAndCloseModal = ({ isOpen, hasError, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const onContinue = () => {
    onClose();
    document.body.style.overflow = 'unset';
  };

  const onExit = () => {
    dispatch(logoutUser());
    document.body.style.overflow = 'unset';
    window.opener = null;
    window.open('', '_self');
    window.close();
  };

  return (
    <Modal
      variant="md"
      isOpen={isOpen}
      onClose={onClose}
      label={t('saveAndClose.title')}
      smallPadding
    >
      {hasError ? (
        <>
          <SaveAndCloseError>
            <SaveAndCloseErrorIcon />
          </SaveAndCloseError>
          <Paragraph color={themeColors.colorTextXLLight} mb={28} mt={14}>
            {t('saveAndClose.error.description')}
          </Paragraph>
          <ButtonPrimary
            onClick={onExit}
            title={t('saveAndClose.error.close')}
          />
        </>
      ) : (
        <>
          <SaveAndCloseSuccess>
            <SaveAndCloseSuccessIcon />
          </SaveAndCloseSuccess>
          <Paragraph color={themeColors.colorTextXLLight} mb={28} mt={14}>
            {t('saveAndClose.success.description')}
          </Paragraph>
          <ButtonGroup>
            <ButtonPrimary
              onClick={onExit}
              title={t('saveAndClose.success.exit')}
            />
            <ButtonPrimary
              onClick={onContinue}
              title={t('saveAndClose.success.continue')}
            />
          </ButtonGroup>
        </>
      )}
    </Modal>
  );
};

SaveAndCloseModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  hasError: PropTypes.bool,
};

export default SaveAndCloseModal;
